import React, { useMemo, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { Button } from '@mui/material';
import { CANCEL_CHANNEL_SUBSCRIPTION } from '../../../gql/subscriptions';
import { ChannelSubscription } from '../../../shared/types';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { getDisplayProperties } from '../../../shared/channelSubscription';

interface ChannelUnsubscribeProps {
    subscription: ChannelSubscription;
    onUpdate?: () => void;
}

export const ChannelUnsubscribe = ({
    subscription,
    onUpdate = () => {},
}: ChannelUnsubscribeProps) => {
    const channel = useMemo(() => subscription.channel, [subscription]);
    const [cancelSubscription] = useMutation<boolean, { channelId: string }>(
        CANCEL_CHANNEL_SUBSCRIPTION
    );
    const [isUnsubscribeDialogShown, setIsUnsubscribeDialogShown] =
        useState(false);

    const handleUnsubscribe = () => {
        cancelSubscription({
            variables: {
                channelId: channel._id,
            },
        }).then(() => onUpdate());
    };

    const { isFreeOpenChannel, isFreeClosedChannel, isPremiumChannel } =
        useMemo(
            () => getDisplayProperties(subscription, channel),
            [subscription, channel]
        );

    return (
        <>
            <ManageTemplateDialog
                title={
                    isPremiumChannel
                        ? 'Are you sure you want to unsubscribe?'
                        : `Unsubscribe from ${channel?.name}?`
                }
                dialogMessage={
                    <>
                        {isPremiumChannel && (
                            <>
                                You are about to unsubscribe from the Premium
                                Channel <b> {channel?.name}</b>. You will have
                                access to the articles in this channel until the
                                end of the billing cycle.
                            </>
                        )}
                        {isFreeOpenChannel && (
                            <>
                                If you stop being a subscriber, you won't
                                receive email notifications when new articles
                                are published in this channel.
                            </>
                        )}
                        {isFreeClosedChannel && (
                            <>
                                If you stop being a subscriber you will lose
                                access, and won’t be able to read articles.
                                Moreover, you won’t receive email notifications
                                when new articles are published in this channel.
                            </>
                        )}
                    </>
                }
                submitBtnText="Yes, unsubscribe"
                cancelBtnText="Cancel"
                dialogOpenState={isUnsubscribeDialogShown}
                onDelete={() => {
                    handleUnsubscribe();
                    setIsUnsubscribeDialogShown(false);
                }}
                submitBtnColorType="primary"
                closeDialog={() => setIsUnsubscribeDialogShown(false)}
            />
            <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setIsUnsubscribeDialogShown(true)}
            >
                Unsubscribe
            </Button>
        </>
    );
};
